import React, { useEffect, useState } from "react";
import master_project from '../assets/images/projects/master2.png';
import qzabre_project from '../assets/images/projects/qzabre_project.png';
import amplifyme from '../assets/images/projects/amplifyme2.png';
import riskon from '../assets/images/projects/riskon.png';
import drone from '../assets/images/projects/drone2.png';
import comsol from '../assets/images/projects/comsol.png';
import pmp_project from '../assets/images/projects/pmp_validation.webp';
import break_project from '../assets/images/projects/adia_breaks.webp';
import qrt_project from '../assets/images/projects/qrt_challenge.svg';
import accelerator_project from '../assets/images/projects/phd2.png';
import trading_infra_project from '../assets/images/projects/trading_infra.svg';
import '../assets/styles/Projects.scss';

function Projects() {
  const [activeProject, setActiveProject] = useState<string | null>(null);

  useEffect(() => {
      document.body.style.overflow = activeProject? "hidden" : "auto";
    }, [activeProject]);

  const projectData: { [key: string]: any } = {
    phd: {
      title: "AI Accelerator Hardware/Software Co-Development — PhD",
      img: accelerator_project,
      description: "Development of AI inference accelerators across the full silicon lifecycle — device simulation, chip design, cleanroom fabrication and system-level integration — together with the corresponding software stack.",
      detailedDescription: "Research and development of novel computing architectures for high-performance AI inference, based on the computational principles of the brain (neuromorphic computing). The work spans the complete silicon lifecycle: device and circuit simulation, chip design, fabrication in the cleanroom (metal evaporation, sputtering, ion-milling, etching), characterisation of the fabricated devices, and system-level implementation on the resulting architecture. The research focuses on memristive crossbar arrays for in-memory computing, in which memory and computation are co-located, thereby avoiding the data movement that dominates energy consumption in conventional accelerators. These analogue circuits are combined with ultra-fast integrated photonics to accelerate matrix-vector multiplication and to reduce computational time and system complexity. A second research thread addresses intra-chip communication, characterising and optimising the on-chip channels with respect to throughput, latency and energy. The architecture requires a dedicated software stack: mapping and compilation of neural networks onto the substrate, algorithms for in-memory computing, and quantisation and sparsity handled within the mapping. Hardware and software are developed in parallel, as the properties of the substrate determine which model architectures can be implemented efficiently. Systems-level and hardware simulations run from the model through scheduling, dataflow and the memory hierarchy, with measured behaviour informing subsequent architectural decisions.",
      skills: ["Chip Design & Device Simulation","Cleanroom Fabrication (evaporation, sputtering, ion-milling, etching)","Memristive Crossbar Arrays","Integrated Photonics","In-Memory Computing & Matrix-Vector Multiplication","Intra-Chip Communication: Throughput, Latency, Energy","Analogue Circuit Design","Hardware/Software Co-Design","Network Mapping & Compilation","Systems-Level & Hardware Simulation"],
      link: "https://hector-fellow-academy.de/en/doctoral-projects/photonic-neuromorphic-circuits-for-artificial-neural-networks/"
    },
    trading: {
      title: "Trading Interface & Infrastructure",
      img: trading_infra_project,
      description: "Real-time trading platform for multi-factor market analysis, with ML models and agentic AI in the decision path.",
      detailedDescription: "End-to-end development of a real-time trading platform for multi-factor market analysis, enhanced with ML models and agentic AI for sentiment analysis and trading decision support. The system covers live market-data ingestion pipelines, risk-signal generation, and the infrastructure needed to deploy time-sensitive investment strategies. Signal computation latency was brought into the millisecond regime through LLM and agentic-AI optimisation, caching, and asynchronous processing.",
      skills: ["Real-Time System Design","Live Market-Data Ingestion","Risk-Signal Generation","Agentic AI & LLM Integration","Low-Latency Optimisation","Caching & Asynchronous Processing","Research-to-Production Deployment"],
      link: "https://www.aam.ch/en/"
    },
    qrt: {
      title: "QRT Asset Allocation Challenge",
      img: qrt_project,
      description: "Top 10% out of 967 — end-to-end ML pipeline predicting next-day return signs across 278 asset allocations.",
      detailedDescription: "Placed in the top 10% out of 967 participants in the QRT Asset Allocation Challenge. I built an end-to-end tree-based AI pipeline to predict next-day return signs across 278 asset allocations, engineering an efficient and stable lean feature set to achieve 52.3% out-of-sample accuracy — balancing predictive power against overfitting on noisy financial data.",
      skills: ["Competitive Quant Research","Tree-Based Modeling","Cross-Asset Return Prediction","Robust Feature Selection","Out-of-Sample Validation"],
      link: "https://www.qube-rt.com/"
    },
    pmp: {
      title: "Algorithmic Trading and Portfolio Management",
      img: pmp_project,
      description: "Real-money portfolio management with systematic trading strategy development across equities, bonds, FX, and futures in cooperation with UBS and QRT.",
      detailedDescription: "Managing a real-money portfolio under cost, liquidity, and risk constraints as part of the Portfolio Management Programme at the University of Zurich in cooperation with UBS and QRT. Since May 2026 as Portfolio Manager, leading the design and deployment of predictive signals across equities, bonds, FX and derivatives, and managing a team of six quantitative analysts for the portfolio implementation. Before that as Quantitative Analyst: researching and implementing statistical signals including momentum, low-beta, and overreaction effects across multiple asset classes, combining statistical models (GARCH, regression) and ML (random forest) to systematically generate alpha, with a focus on backtesting, signal decay monitoring, and Sharpe ratio maximisation — achieving Sharpe > 1.",
      skills: ["Portfolio Management & Optimization","People Management & Team Leadership","Systematic Strategy Development","Statistical Signal Research","Multi-Asset Class Investing","Backtesting & Strategy Evaluation","Sharpe Ratio Maximization & Alpha Generation","Risk, Cost & Liquidity Constraint Management","Quantitative Research & Factor Investing", "FX & Derivatives Markets"],
      link: "https://www.cpm.uzh.ch/en.html"
    },
    break: {
      title: "Structural Break Detection in Time Series",
      img: break_project,
      description: "Developed a structural break detection algorithm for high-volume time series, combining ML and statistical methods.",
      detailedDescription: "Competed in a data science challenge hosted by ADIA Lab and Crunch Labs, developing an algorithm to detect structural breaks and regime shifts in high-volume time series data. Built a hybrid model combining gradient boosting, bagging, GARCH modeling, and spectral analysis for robust regime-shift forecasting.",
      skills: ["Machine Learning Model Development","Statistical Modeling, Forecasting & ML Predictions","Gradient Boosting & Bagging","High-Volume Data Processing","Competitive Data Science","Time-Series Analysis"],
      link: "https://www.adialab.ae/"
    },
    arbitrage: {
      title: "Times Series Arbitrage Algorithm",
      img: amplifyme,
      description: "Developed an equity arbitrage detection algorithm at a Millennium-hosted trading event of AmplifyME.",
      detailedDescription: "During a student trading competition event hosted by Millennium and AmplifyME, I developed an arbitrage detection algorithm with automated execution for equities. Competed against other teams across key performance metrics including PnL and trading frequency, combining quantitative modeling with real-time strategy optimization.",
      skills: ["Quantitative Trading Algorithm Development","PnL Optimization","Statistical & Quantitative Analysis","Arbitrage Detection & Strategy Design","Team Collaboration in a Trading Environment"],
      link: "https://amplifyme.com/",
      link2: "https://www.mlp.com/",
      linkName1: "AmplifyME",
      linkName2: "Millennium"
    },
    risk: {
      title: "Financial Risk Modeling Challenge",
      img: riskon,
      description: "Development of an AI-backed fraud and deepfake detection solution for private banking during a hackathon.",
      detailedDescription: "Developed a novel approach to detect and mitigate deepfake and AI-driven fraud in a private banking context during a 2-day hackathon. Designed and pitched an AI-backed solution combining fraud detection methodology with practical implementation considerations. With this we addressed emerging threats in financial security under GDPR & FADP compliance.",
      skills: ["AI-Backed Solution Design","Deepfake & Anomaly Detection", "Fraud Detection","Team Collaboration Under Pressure","Presentation & Pitching","Rapid Prototyping & Ideation"],
      link: "https://www.riskon.ch/",
      img_source1: "https://data-innovation.org/riskon-promoting-the-further-development-of-risk-management-in-switzerland/"
    },
    master: {
      title: "Master Project",
      img: master_project,
      description: "Quantitative modeling to nanostructure dynamics and experimental nano-optics setup devlopment.",
      detailedDescription: "Applied quantitative mathematical frameworks to model nanoscale dynamics, experimentally validated through a self-built nano-optical setup. Designed and implemented a novel laser scanning technique achieving enhanced signal-to-noise ratio, enabling imaging at previously inaccessible scales. The project combined rigorous theory-to-data validation and high-precision experimental instrumentation, supported by statistical analysis, frequency analysis, and time-series analysis.",
      skills: ["Quantitative Mathematical Modeling","Theory-to-Data Validation","Experimental Design & Instrumentation","Novel Method Development","Statistical Analysis","Dynamic Systems Characterization"],
      link: "https://www.np.phy.cam.ac.uk/"
    },
    intern: {
      title: "R&D Projects",
      img: qzabre_project,
      description: "Software and Hardware development for NV quantum sensing applications.",
      detailedDescription: "Developed and implemented hard/- and software for an NV quantum scanning microscope to enhance sensing performance and software efficiency. Contributions were directly integrated into the final customer product. NDA applies.",
      skills: ["Software and Hardware Co-Development","Data Acquisition & Analysis","Rapid Prototyping & Iteration","Quantum Sensing & Instrumentation", "End-to-End Product Development"],
      link: "https://qzabre.com/en/products/quantum-scanning-microscope",
      img_source1: "https://www.qzabre.com/"
    },
    bachelor: {
      title: "Physical Modeling",
      img: comsol,
      description: "Modeled and optimized optical devices for future photonic chip designs for high-speed communication.",
      detailedDescription: "Designed and simulated novel optical devices under physical fabrication constraints, spanning photonic and plasmonic chip solutions for high-speed communication applications, including out-of-plane edge couplers and novel approaches for plasmonic on chip coupling. NDA applies.",
      skills: ["Numerical Modeling & Simulation", "Quantitative Optimization", "Physical Constraint Analysis","Computational Problem Solving","COMSOL / Lumerical"],  
      link: "",
      img_source1: "https://www.comsol.com/",
    },
    drone: {
      title: "Autonomous Drone Control Algorithm",
      img: drone,
      description: "Developed and implemented a control algorithm in C for autonomous drone flight.",
      detailedDescription: "As part of a two-person team, I developed and implemented a control algorithm enabling a drone to autonomously take off, stabilize at a predefined altitude, hover reliably, and land safely. The project combined mathematical modeling of the system dynamics with the implementation of the control logic in C, translating theoretical control principles into a real-world embedded application.",  
      skills: ["Mathematical Modeling & Simulation", "Algorithm Design & Implementation", "C Programming","Embedded & Low-Latency Systems","Signal Processing & Noise Filtering", "Systems Testing & Validation"],
      link: "",
      img_source1: "https://blogs.icrc.org/law-and-policy/2022/03/15/consumer-drones-conflict-ihl/",
      img_source2: "https://robotics.stackexchange.com/questions/18592/why-is-the-kalman-filter-a-filter-and-not-a-control-system"
    }
  };

  return (
    <div className="projects-container" id="projects">
      <h1>Projects</h1>
      <div className="projects-grid">
        {Object.keys(projectData).map((key) => (
            <div key={key} className="project">
                <div
                className="image-wrapper"
                onClick={() => setActiveProject(key)}
                >
                <img
                    src={projectData[key].img}
                    className="project-image"
                    alt="thumbnail"
                />
                <div className="details-text">Show Details</div>
                </div>

                <h2>{projectData[key].title}</h2>
                <p>{projectData[key].description}</p>
            </div>
            ))}
      </div>

      {/* Overlay */}
      {activeProject && (
        <div className="hover-page-overlay" onClick={() => setActiveProject(null)}>
          <div className="hover-page-content" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <button
              className="close-btn"
              onClick={() => setActiveProject(null)}
            >
              ✕
            </button>
            <div className="hover-page-header">
              <h1>{projectData[activeProject].title}</h1>
              <img
                src={projectData[activeProject].img}
                className="hover-page-image"
              />
            </div>
            <p>{projectData[activeProject].detailedDescription}</p>
            <ul>
              {projectData[activeProject].skills.map((skill: string, index: number) => (
                <li key={index}>{skill}</li>
              ))}
            </ul>

            {/* Single link */}
            {projectData[activeProject].link && !projectData[activeProject].link2 && (
              <a
                href={projectData[activeProject].link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-page-link-btn"
              >
                → More info
              </a>
            )}

            {/* Dual links */}
            {projectData[activeProject].link && projectData[activeProject].link2 && (
              <div>
                <a
                  href={projectData[activeProject].link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → More info {projectData[activeProject].linkName1 || "Link 1"}
                </a>
                <br />
                <a
                  href={projectData[activeProject].link2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → More info {projectData[activeProject].linkName2 || "Link 2"}
                </a>
              </div>
            )}

            {/* Image source */}
            {projectData[activeProject].img_source1 && (
              <p style={{ fontSize: "0.65rem", color: "#999", marginTop: "1.5rem" }}>
                Image source: {projectData[activeProject].img_source1}
              </p>
            )}
            {/* Single Image source */}
            {projectData[activeProject].img_source1 && !projectData[activeProject].img_source2 && (
              <p style={{ fontSize: "0.65rem", color: "#999", marginTop: "1.5rem" }}>
                Image source: {projectData[activeProject].img_source1}
                {projectData[activeProject].img_source2 && `, ${projectData[activeProject].img_source2}`}
              </p>
            )}

            {/* Dual Image sources */}
            {projectData[activeProject].img_source1 && projectData[activeProject].img_source2 && (
              <p style={{ fontSize: "0.65rem", color: "#999", marginTop: "1.5rem" }}>
                Image source 2: {projectData[activeProject].img_source2}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;